import { Check, Compass } from "lucide-react";
import { BUUKD_EXPLORE_STORE_LINKS, BUUKD_PRO_STORE_LINKS } from "@/lib/app-store-links";

const APP_STORE_BADGE = "/badges/download-on-the-app-store.svg";
const GOOGLE_PLAY_BADGE = "/badges/get-it-on-google-play.svg";

/** Apple + Google badges share the same rendered height (40px min per store guidelines). */
const BADGE_CLASS = "h-10 w-auto block";

type AppCardProps = {
  name: string;
  tagline: string;
  description: string;
  iconSrc: string;
  iconAlt: string;
  accentClass: string;
  highlights: string[];
  iosUrl: string;
  androidUrl: string;
};

function AppStoreBadge({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-10 items-center transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
      aria-label="Download on the App Store"
    >
      <img src={APP_STORE_BADGE} alt="" className={BADGE_CLASS} />
    </a>
  );
}

function GooglePlayBadge({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-10 items-center transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
      aria-label="Get it on Google Play"
    >
      <img src={GOOGLE_PLAY_BADGE} alt="" className={BADGE_CLASS} />
    </a>
  );
}

function AppCard({
  name,
  tagline,
  description,
  iconSrc,
  iconAlt,
  accentClass,
  highlights,
  iosUrl,
  androidUrl,
}: AppCardProps) {
  return (
    <article className="flex h-full flex-col rounded-3xl border border-border bg-card p-6 shadow-soft sm:p-8">
      <div className="flex items-start gap-4">
        <img
          src={iconSrc}
          alt={iconAlt}
          className="h-16 w-16 shrink-0 rounded-2xl object-cover shadow-soft-md"
        />
        <div>
          <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${accentClass}`}>{tagline}</p>
          <h3 className="mt-1 text-2xl font-bold text-foreground">{name}</h3>
        </div>
      </div>

      <p className="mt-5 text-muted-foreground leading-relaxed">{description}</p>

      <ul className="mt-5 space-y-2.5">
        {highlights.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-foreground">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${accentClass}`} aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <AppStoreBadge href={iosUrl} />
        <GooglePlayBadge href={androidUrl} />
      </div>
    </article>
  );
}

export function MobileAppsSection() {
  return (
    <section id="apps" className="py-16 lg:py-24 bg-subtle-gray relative overflow-hidden">
      <div className="absolute inset-0 bg-dotted opacity-30" />
      <div className="container-app relative">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-pink-500">
            Mobile apps
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground leading-tight">
            Buukd Pro for your business. Buukd Explore for your clients.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Two apps, one platform — run your salon or studio from Buukd Pro, and let customers discover
            looks, find you nearby, and book through Buukd Explore.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <AppCard
            name="Buukd Pro"
            tagline="For business owners"
            description="Your command center on the go. Manage bookings, customers, payments, staff, and marketing without being tied to a laptop."
            iconSrc="/BuukdPro_AppIcon.png"
            iconAlt="Buukd Pro app icon"
            accentClass="text-sky-600"
            highlights={[
              "View and manage appointments in real time",
              "Get notified when customers book or pay",
              "Run your business from iPhone or Android",
            ]}
            iosUrl={BUUKD_PRO_STORE_LINKS.ios}
            androidUrl={BUUKD_PRO_STORE_LINKS.android}
          />

          <AppCard
            name="Buukd Explore"
            tagline="For customers"
            description="A scroll-first way to discover beauty and wellness. Browse looks, find salons near you, and book the styles you love in a few taps."
            iconSrc="/BuukdExploreIcon.png"
            iconAlt="Buukd Explore app icon"
            accentClass="text-pink-500"
            highlights={[
              "Discover salons and trending looks near you",
              "Book services directly from the feed",
              "Get booking confirmations on your phone",
            ]}
            iosUrl={BUUKD_EXPLORE_STORE_LINKS.ios}
            androidUrl={BUUKD_EXPLORE_STORE_LINKS.android}
          />
        </div>

        <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-muted-foreground">
          <Compass className="h-4 w-4 text-pink-500 shrink-0" aria-hidden />
          Available on iOS and Android — free to download.
        </p>
      </div>
    </section>
  );
}
